import type { ReactNode } from 'react';

import { useT } from '@/i18n';
import { MASK_DOTS } from '@/lib/secretMask';

import { EyeIcon, EyeOffIcon } from './Icons';
import styles from './passwordPlate.module.css';
import { PassworkMark } from './PassworkMark';

/**
 * A password on a plate as tall as the main button: the Passwork key, the password — dots until it is shown — and,
 * at the right, small outlined buttons of one size: the eye, then whatever else the place needs (copy, remove).
 */
export function PasswordPlate({
  password,
  shown,
  onToggle,
  children,
  testId,
}: {
  readonly password: string;
  readonly shown: boolean;
  readonly onToggle: () => void;
  /** More buttons after the eye, made with PlateButton. */
  readonly children?: ReactNode;
  readonly testId?: string;
}): React.JSX.Element {
  const t = useT();
  return (
    <div className={styles.plate} data-testid={testId}>
      <PassworkMark className={styles.mark} />
      {shown ? (
        <span className={styles.text} data-testid="password-text">
          {password}
        </span>
      ) : (
        <span className={styles.dots} role="img" aria-label={t('passwordPlate.hidden')} data-testid="password-dots">
          {Array.from({ length: MASK_DOTS }, (_, index) => (
            <i key={index} />
          ))}
        </span>
      )}
      <PlateButton label={shown ? t('passwordPlate.hide') : t('passwordPlate.show')} onClick={onToggle} pressed={shown}>
        {shown ? <EyeOffIcon size={17} /> : <EyeIcon size={17} />}
      </PlateButton>
      {children}
    </div>
  );
}

/** One small outlined button on the plate. */
export function PlateButton({
  label,
  onClick,
  children,
  pressed,
  testId,
  className,
}: {
  readonly label: string;
  readonly className?: string | undefined;
  readonly onClick: () => void;
  readonly children: ReactNode;
  readonly pressed?: boolean;
  readonly testId?: string;
}): React.JSX.Element {
  return (
    <button type="button" className={[styles.button, className ?? ''].join(' ')} aria-label={label} title={label} aria-pressed={pressed} onClick={onClick} data-testid={testId}>
      {children}
    </button>
  );
}
